import { createSelector } from 'reselect';

const selectModelPortfolios = (s) => s.modelPortfolios.items;
const selectModelPortfoliosByRiskProfiles = (s) => s.modelPortfolios.itemsByRiskProfiles;
const selectMandatesProperty = (s, property) => s.modelPortfolios[property];
const selectRiskProfiles = (state) => state.statics.riskProfiles;

export const selectModelPortfolio = createSelector(
  (_, id) => id,
  selectModelPortfolios,
  (s) => selectMandatesProperty(s, 'holdings'),
  (s) => selectMandatesProperty(s, 'kpis'),
  (s) => selectMandatesProperty(s, 'performances'),
  (s) => selectMandatesProperty(s, 'sectorAllocation'),
  (s) => selectMandatesProperty(s, 'geoAllocation'),
  (s) => selectMandatesProperty(s, 'assetClassAllocation'),
  (s) => selectMandatesProperty(s, 'currencies'),
  (s) => selectMandatesProperty(s, 'maxDrawdowns'),
  (
    id,
    modelPortfolios,
    holdings,
    kpis,
    performances,
    sectorAllocation,
    geoAllocation,
    assetClassAllocation,
    currencies,
    maxDrawdowns
  ) => {
    if (!modelPortfolios) return undefined;

    const m = modelPortfolios.find((c) => c.id === id);
    if (!m) return null;
    const perf = performances[m.id] ?? {};
    return {
      ...m,
      holdings: holdings[m.id] ?? [],
      currencies: currencies[m.id] ?? [],
      sectorAllocation: sectorAllocation[m.id] ?? [],
      geoAllocation: geoAllocation[m.id] ?? [],
      assetClassAllocation: assetClassAllocation[m.id] ?? [],
      kpis: kpis[m.id] ?? {},
      maxDrawdown: maxDrawdowns[m.id] ?? undefined,
      ...perf,
    };
  }
);

export const selectModelPortfoliosWithKpis = createSelector(
  selectModelPortfolios,
  (s) => selectMandatesProperty(s, 'kpis'),
  (modelPortfolios, kpis) => {
    if (!modelPortfolios) return undefined;

    return modelPortfolios.map((m) => ({
      ...m,
      ...kpis[m.id],
    }));
  }
);

export const selectModelPortfoliosByRiskProfileId = createSelector(
  selectModelPortfoliosByRiskProfiles,
  (_, riskProfileId) => riskProfileId,
  (modelPortfolios, riskProfileId) =>
    modelPortfolios[riskProfileId] ? modelPortfolios[riskProfileId].filter((m) => m.published) : []
);

export const selectLowerRiskModelPortfoliosByRiskProfileId = createSelector(
  selectModelPortfolios,
  selectRiskProfiles,
  (_, riskProfileId) => riskProfileId,
  (modelPortfolios, riskProfiles, riskProfileId) => {
    if (modelPortfolios && riskProfiles && riskProfileId) {
      const targetProfile = riskProfiles.find((s) => s.id === riskProfileId);
      const allowedRiskProfiles = riskProfiles
        .filter((s) => {
          const selectedRiskProfile = riskProfiles.find((profile) => profile.id === s.id)
          return selectedRiskProfile.riskLevel <= targetProfile.riskLevel
        })
        .map((s) => s.id);

      return modelPortfolios.filter(
        (s) => s.published && allowedRiskProfiles.includes(s.riskProfileId)
      );
    }
    return [];
  }
);
